/*
 * A line diff, computed in the browser.
 *
 * Needed for one case only: the conventions file. `GET /schema/revisions` returns
 * each revision's full `body_md` and no diff between them, so comparing two
 * revisions is either done here or not offered. It is worth doing here — the file is
 * a few hundred lines, both versions are already in hand, and asking this app's
 * server to compute it would be exactly the kind of derivation that belongs
 * upstream. Page diffs are NOT computed this way: those come from the API, which
 * has the git history and can tell added from renamed.
 *
 * Plain LCS rather than Myers: at conventions-file size the difference is
 * unmeasurable, and this is fifteen lines that anyone can check.
 */
import type { DiffHunk, DiffLine } from '../../../api/knowledgeBases.ts'

/** Past this many lines the quadratic table stops being free; degrade instead. */
const LCS_LINE_CAP = 1_500

/** Unchanged lines kept around each change, so a hunk has context. */
const CONTEXT = 3

export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split('\n')
  const b = after.split('\n')

  if (a.length > LCS_LINE_CAP || b.length > LCS_LINE_CAP) {
    return [
      ...a.map((text): DiffLine => ({ kind: 'del', text })),
      ...b.map((text): DiffLine => ({ kind: 'add', text })),
    ]
  }

  // lcs[i * stride + j] = length of the longest common subsequence of a[i:], b[j:].
  // Flat rather than nested so the hot loop indexes one typed array.
  const stride = b.length + 1
  const lcs = new Int32Array((a.length + 1) * stride)
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i * stride + j] =
        a[i] === b[j]
          ? lcs[(i + 1) * stride + j + 1]! + 1
          : Math.max(lcs[(i + 1) * stride + j]!, lcs[i * stride + j + 1]!)
    }
  }

  const lines: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      lines.push({ kind: 'ctx', text: a[i] as string })
      i += 1
      j += 1
    } else if (lcs[(i + 1) * stride + j]! >= lcs[i * stride + j + 1]!) {
      lines.push({ kind: 'del', text: a[i] as string })
      i += 1
    } else {
      lines.push({ kind: 'add', text: b[j] as string })
      j += 1
    }
  }
  while (i < a.length) lines.push({ kind: 'del', text: a[i++] as string })
  while (j < b.length) lines.push({ kind: 'add', text: b[j++] as string })
  return lines
}

/**
 * Group a line diff into hunks, dropping runs of untouched context.
 *
 * Without this a two-line edit to a four-hundred-line conventions file renders as
 * four hundred lines, and the reader scrolls looking for the green.
 */
export function toHunks(lines: DiffLine[], context = CONTEXT): DiffHunk[] {
  const changed = lines.map((line) => line.kind === 'add' || line.kind === 'del')
  if (!changed.some(Boolean)) return []

  const keep = new Array<boolean>(lines.length).fill(false)
  for (let index = 0; index < lines.length; index += 1) {
    if (!changed[index]) continue
    for (let near = index - context; near <= index + context; near += 1) {
      if (near >= 0 && near < lines.length) keep[near] = true
    }
  }

  const hunks: DiffHunk[] = []
  let current: DiffLine[] | null = null
  let start = 0
  for (let index = 0; index < lines.length; index += 1) {
    if (keep[index]) {
      if (current === null) {
        current = []
        start = index
      }
      current.push(lines[index] as DiffLine)
    } else if (current !== null) {
      hunks.push({ header: `@@ строки ${start + 1}–${index}`, lines: current })
      current = null
    }
  }
  if (current !== null) {
    hunks.push({ header: `@@ строки ${start + 1}–${lines.length}`, lines: current })
  }
  return hunks
}

export function diffHunks(before: string, after: string): DiffHunk[] {
  return toHunks(diffLines(before, after))
}

export function countChanges(lines: DiffLine[]): { added: number; removed: number } {
  let added = 0
  let removed = 0
  for (const line of lines) {
    if (line.kind === 'add') added += 1
    else if (line.kind === 'del') removed += 1
  }
  return { added, removed }
}
